<template>
  <div
    ref="rootRef"
    :class="rootClasses"
    role="alert"
    aria-live="assertive"
  >
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 px-4 md:px-6 py-2 sm:py-2.5">
      <div class="flex items-start sm:items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
        <div
          class="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center flex-shrink-0 rounded-full bg-widget-icon mt-0.5 sm:mt-0"
        >
          <i class="ri-spy-line text-lg sm:text-xl text-widget-icon" aria-hidden="true" />
        </div>
        <div class="min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-2">
          <p class="text-[13px] sm:text-sm font-semibold text-badge-blue whitespace-nowrap">
            You are impersonating
            <strong class="font-bold underline decoration-current decoration-2 underline-offset-[3px]">
              {{ impersonatedUserName }}
            </strong>
          </p>
          <span class="hidden sm:inline text-badge-blue opacity-50 select-none" aria-hidden="true">·</span>
          <p class="text-[11px] sm:text-xs text-badge-blue opacity-80 whitespace-nowrap">
            Actions are being performed using this user's permissions.
          </p>
        </div>
      </div>

      <button
        type="button"
        class="flex items-center justify-center gap-1.5 px-2.5 py-1.5 sm:px-3 rounded-lg bg-danger text-danger-foreground text-xs sm:text-[12.5px] font-medium leading-[1.4] hover:bg-danger-hover active:scale-[0.97] transition-all cursor-pointer whitespace-nowrap flex-shrink-0 sm:self-center self-end focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-danger)] focus-visible:outline-offset-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
        aria-label="Stop impersonating"
        :disabled="stopping"
        @click="emit('stop')"
      >
        <i class="ri-stop-circle-line text-sm leading-none" aria-hidden="true" />
        Stop impersonating
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'

const IMPERSONATION_BANNER_HEIGHT_VAR = '--impersonation-banner-height'

const props = withDefaults(
  defineProps<{
    impersonatedUserName: string
    syncLayoutOffset?: boolean
    position?: 'fixed' | 'static'
    stopping?: boolean
  }>(),
  {
    syncLayoutOffset: true,
    position: 'fixed',
    stopping: false,
  }
)

const emit = defineEmits<{
  stop: []
}>()

const rootRef = ref<HTMLElement | null>(null)
let resizeObserver: ResizeObserver | null = null

const rootClasses = computed(() => {
  const positionClasses =
    props.position === 'fixed'
      ? 'fixed top-0 left-0 right-0 z-[100]'
      : 'relative z-auto w-full'

  return [positionClasses, 'bg-badge-blue'].join(' ')
})

function updateLayoutOffset(): void {
  if (!props.syncLayoutOffset || !rootRef.value) {
    return
  }

  document.documentElement.style.setProperty(
    IMPERSONATION_BANNER_HEIGHT_VAR,
    `${rootRef.value.offsetHeight}px`
  )
}

function clearLayoutOffset(): void {
  if (!props.syncLayoutOffset) {
    return
  }

  document.documentElement.style.setProperty(IMPERSONATION_BANNER_HEIGHT_VAR, '0px')
}

onMounted(() => {
  if (!props.syncLayoutOffset || !rootRef.value) {
    return
  }

  resizeObserver = new ResizeObserver(() => {
    updateLayoutOffset()
  })
  resizeObserver.observe(rootRef.value)
  updateLayoutOffset()
})

onUnmounted(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
  clearLayoutOffset()
})
</script>
